import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Bot, Loader2, ShieldAlert, CheckCircle2, AlertTriangle, History, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const SEMAFORO_COLOR = {
  VERDE: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/40", dot: "bg-emerald-400" },
  AMARELO: { bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/40", dot: "bg-amber-400" },
  VERMELHO: { bg: "bg-red-500/15", text: "text-red-300", border: "border-red-500/40", dot: "bg-red-400" },
};

const STATUS_COLOR = {
  APROVADO: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  ATENCAO: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  "ATENÇÃO": "bg-amber-500/15 text-amber-300 border-amber-500/40",
  REPROVADO: "bg-red-500/15 text-red-300 border-red-500/40",
};

const RISCO_COLOR = {
  BAIXO: "text-emerald-400",
  MEDIO: "text-amber-400",
  "MÉDIO": "text-amber-400",
  ALTO: "text-red-400",
};

function formatBRL(v) {
  if (v == null || isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function ScoreBar({ value, label }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const color = v >= 70 ? "bg-emerald-400" : v >= 40 ? "bg-amber-400" : "bg-red-400";
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-[#A3A3A3]">
        <span className="uppercase tracking-wider">{label}</span>
        <span className="font-mono text-white">{v}/100</span>
      </div>
      <div className="mt-1.5 h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${v}%` }}/>
      </div>
    </div>
  );
}

function ResultCard({ result, createdAt, modelLabel }) {
  if (!result) return null;
  const sem = SEMAFORO_COLOR[result.semaforo_antifraude] || SEMAFORO_COLOR.AMARELO;
  const alerts = result.sinais_de_alerta || [];
  const positivos = result.pontos_positivos || [];
  return (
    <div className={`rounded-md border ${sem.border} ${sem.bg} p-5 space-y-4`} data-testid="ai-result-card">
      {/* Topo: semáforo + status + risco */}
      <div className="flex flex-wrap items-center gap-3">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${sem.bg} border ${sem.border}`} data-testid="ai-semaforo">
          <span className={`w-2 h-2 rounded-full ${sem.dot}`}></span>
          <span className={`text-xs font-semibold tracking-wider ${sem.text}`}>SEMÁFORO {result.semaforo_antifraude}</span>
        </div>
        <span className={`badge-base border ${STATUS_COLOR[result.status_ia] || ""}`} data-testid="ai-status">
          {result.status_ia}
        </span>
        <span className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
          Risco: <span className={`font-semibold ${RISCO_COLOR[result.risco_ia] || "text-white"}`}>{result.risco_ia}</span>
        </span>
        {createdAt && (
          <span className="text-[11px] text-[#737373] ml-auto">
            {new Date(createdAt).toLocaleString("pt-BR")} {modelLabel && <>· {modelLabel}</>}
          </span>
        )}
      </div>

      {/* Scores */}
      <div className="grid sm:grid-cols-2 gap-4">
        <ScoreBar value={result.score_ia} label="Score antifraude" />
        <ScoreBar value={result.score_confianca} label="Confiança da análise" />
      </div>

      {/* Resumo */}
      {result.resumo_ia && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#737373]">Resumo executivo</div>
          <p className="text-sm text-white mt-1.5 leading-relaxed">{result.resumo_ia}</p>
        </div>
      )}

      {/* Justificativa */}
      {result.justificativa_ia && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#737373]">Justificativa</div>
          <p className="text-[13.5px] text-[#D4D4D4] mt-1.5 leading-relaxed">{result.justificativa_ia}</p>
        </div>
      )}

      {/* Sinais de alerta */}
      {alerts.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-red-300 flex items-center gap-1.5">
            <ShieldAlert size={12}/> Sinais de alerta ({alerts.length})
          </div>
          <ul className="mt-2 space-y-1.5">
            {alerts.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-red-200">
                <AlertTriangle size={13} className="text-red-400 flex-shrink-0 mt-0.5"/>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Pontos positivos */}
      {positivos.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
            <CheckCircle2 size={12}/> Pontos positivos ({positivos.length})
          </div>
          <ul className="mt-2 space-y-1.5">
            {positivos.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-emerald-200">
                <CheckCircle2 size={13} className="text-emerald-400 flex-shrink-0 mt-0.5"/>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Análises específicas (geo, docs, duplicidade) */}
      <div className="grid sm:grid-cols-2 gap-4 pt-2">
        {result.comparacao_geo_endereco && (
          <div className="p-3 rounded-md bg-[#0a0a0a] border border-[#1f1f1f]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#737373]">GPS × Endereço informado</div>
            <div className={`text-xs mt-1 font-semibold ${result.comparacao_geo_endereco.coerente ? "text-emerald-300" : "text-red-300"}`}>
              {result.comparacao_geo_endereco.coerente ? "Coerente" : "Divergente"}
            </div>
            <p className="text-[12px] text-[#A3A3A3] mt-1 leading-snug">{result.comparacao_geo_endereco.comentario}</p>
          </div>
        )}
        {result.verificacao_duplicidade && (
          <div className="p-3 rounded-md bg-[#0a0a0a] border border-[#1f1f1f]">
            <div className="text-[10.5px] uppercase tracking-wider text-[#737373]">Verificação de duplicidade</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {result.verificacao_duplicidade.cpf_duplicado && <span className="badge-base border bg-red-500/10 text-red-300 border-red-500/30">CPF duplicado</span>}
              {result.verificacao_duplicidade.telefone_duplicado && <span className="badge-base border bg-red-500/10 text-red-300 border-red-500/30">Telefone duplicado</span>}
              {result.verificacao_duplicidade.endereco_similar && <span className="badge-base border bg-amber-500/10 text-amber-300 border-amber-500/30">Endereço similar</span>}
              {!result.verificacao_duplicidade.cpf_duplicado && !result.verificacao_duplicidade.telefone_duplicado && !result.verificacao_duplicidade.endereco_similar && (
                <span className="text-[12px] text-emerald-300">Sem duplicidades.</span>
              )}
            </div>
            <p className="text-[12px] text-[#A3A3A3] mt-1.5 leading-snug">{result.verificacao_duplicidade.comentario}</p>
          </div>
        )}
      </div>

      {/* Análise de documentos */}
      {result.analise_documentos && (
        <div className="p-3 rounded-md bg-[#0a0a0a] border border-[#1f1f1f]">
          <div className="text-[10.5px] uppercase tracking-wider text-[#737373] mb-2">Análise dos documentos</div>
          <div className="space-y-2">
            {[
              ["RG/CNH", result.analise_documentos.rg_cnh],
              ["Selfie com documento", result.analise_documentos.selfie_doc],
              ["Comprovante de residência", result.analise_documentos.comprovante_residencia],
              ["Foto da casa", result.analise_documentos.foto_casa],
            ].filter(([_, v]) => v).map(([k, v]) => (
              <div key={k} className="text-[12.5px]">
                <span className="text-[#A3A3A3]">{k}: </span>
                <span className={v === "não enviado" ? "text-[#737373] italic" : "text-white"}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer: valor + próxima ação */}
      <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-[#1f1f1f]">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#737373]">Valor sugerido</div>
          <div className="font-display text-xl font-bold text-[#D4AF37] mt-0.5" data-testid="ai-suggested-amount">{formatBRL(result.valor_sugerido_ia)}</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#737373]">Próxima ação recomendada</div>
          <div className="text-[13.5px] mt-0.5 text-white">{result.recomendacao_proxima_acao || "—"}</div>
        </div>
      </div>
    </div>
  );
}

export default function AIAnalysisSection({ clientId, snapshot, onUpdated }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/ai-analyses`);
      setHistory(data || []);
      // Pega a última análise concluída (com result) como "latest"
      const done = (data || []).find(a => a && a.result);
      if (done) setLatest(prev => prev || done);
      return data || [];
    } catch (e) {
      return [];
    }
  }, [clientId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const run = async () => {
    setAnalyzing(true);
    setError("");
    let pendingId = null;
    try {
      const { data } = await api.post(`/clients/${clientId}/ai-analysis`);
      pendingId = data?.pending_id;
      toast.success("Análise iniciada em segundo plano");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Falha ao iniciar análise.";
      setError(msg);
      toast.error(msg);
      setAnalyzing(false);
      return;
    }
    // Polling até a análise terminar ou expirar (~3 min)
    const started = Date.now();
    const maxMs = 3 * 60 * 1000;
    let lastKnownIds = new Set(history.map(h => h.id));
    const tick = async () => {
      const data = await loadHistory();
      // Sucesso: surgiu uma análise nova com result
      const fresh = (data || []).find(a => a.result && !lastKnownIds.has(a.id));
      if (fresh) {
        setLatest(fresh);
        toast.success("Análise concluída");
        onUpdated && onUpdated();
        setAnalyzing(false);
        return;
      }
      // Falha registrada no pending
      if (pendingId) {
        const failed = (data || []).find(a => a.id === pendingId && a.status === "failed");
        if (failed) {
          setError(failed.error || "Análise falhou.");
          toast.error("Análise falhou");
          setAnalyzing(false);
          return;
        }
      }
      if (Date.now() - started > maxMs) {
        setError("Análise demorou mais que o esperado. Atualize a página em alguns instantes.");
        setAnalyzing(false);
        return;
      }
      setTimeout(tick, 6000);
    };
    setTimeout(tick, 6000);
  };

  const result = latest?.result;
  const hasAny = !!result || !!snapshot?.ai_status;

  // Detecta cliente recém-criado (< 5 min) sem análise → análise automática provavelmente em andamento
  let pendingAuto = false;
  if (!hasAny && snapshot?.created_at) {
    try {
      const ageMs = Date.now() - new Date(snapshot.created_at).getTime();
      pendingAuto = ageMs >= 0 && ageMs < 5 * 60 * 1000;
    } catch (_) { /* noop */ }
  }

  return (
    <section className="fc-card p-6 sm:p-7" data-testid="ai-analysis-section">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
            <Bot className="text-[#D4AF37]" size={20}/>
          </div>
          <div>
            <h2 className="font-display text-[17px] font-semibold tracking-tight">Cão farejador — análise antifraude</h2>
            <p className="text-[12.5px] text-[#A3A3A3] mt-0.5">Parecer técnico gerado por IA. Não substitui aprovação humana.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {history.length > 0 && (
            <button
              onClick={() => setShowHistory(s => !s)}
              className="fc-btn-ghost flex items-center gap-1.5 text-[12.5px]"
              data-testid="ai-toggle-history"
            >
              <History size={13}/> Histórico ({history.length})
              {showHistory ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
            </button>
          )}
          <button
            onClick={run}
            disabled={analyzing}
            className="fc-btn-gold flex items-center gap-2"
            data-testid="ai-analyze-button"
          >
            {analyzing
              ? <><Loader2 size={15} className="animate-spin"/> Analisando…</>
              : <><Sparkles size={15}/> {hasAny ? "Reanalisar com IA" : "Analisar com IA"}</>}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3" data-testid="ai-error">
          {error}
        </div>
      )}

      {analyzing && !result && (
        <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] p-6 text-center" data-testid="ai-loading">
          <Loader2 className="inline animate-spin text-[#D4AF37]" size={20}/>
          <p className="text-[13px] text-[#A3A3A3] mt-3">Analisando dados, documentos, localização e duplicidades…</p>
          <p className="text-[11px] text-[#737373] mt-1">Pode levar de 5 a 15 segundos.</p>
        </div>
      )}

      {!analyzing && !result && !snapshot?.ai_status && (
        <div className="rounded-md border border-dashed border-[#262626] bg-[#0a0a0a] p-6 text-center" data-testid="ai-empty">
          <Bot size={24} className="mx-auto text-[#737373]"/>
          {pendingAuto ? (
            <>
              <p className="text-[13px] text-[#D4AF37] mt-3 flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin"/> Análise automática em andamento…
              </p>
              <p className="text-[12px] text-[#737373] mt-1">A IA está processando este cadastro em segundo plano. Recarregue a página em ~30s para ver o parecer.</p>
            </>
          ) : (
            <>
              <p className="text-[13px] text-[#A3A3A3] mt-3">Este cliente ainda não foi analisado pela IA.</p>
              <p className="text-[12px] text-[#737373] mt-1">Clique em <strong className="text-white">Analisar com IA</strong> para gerar um parecer antifraude.</p>
            </>
          )}
        </div>
      )}

      {result && (
        <ResultCard result={result} createdAt={latest.created_at} modelLabel={latest.model}/>
      )}

      {/* Histórico */}
      {showHistory && history.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[#1f1f1f] space-y-4" data-testid="ai-history-list">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Histórico ({history.length})</div>
          {history.slice(0, 5).map(h => (
            <details key={h.id} className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] p-4">
              <summary className="cursor-pointer flex items-center justify-between text-[12.5px]">
                <span className="text-[#A3A3A3]">
                  {new Date(h.created_at).toLocaleString("pt-BR")}
                  {" · "}<span className="text-white">{h.result?.status_ia}</span>
                  {" · "}<span className="text-[#D4AF37]">{h.result?.semaforo_antifraude}</span>
                  {" · score "}<span className="text-white">{h.result?.score_ia}</span>
                </span>
                <span className="text-[10.5px] text-[#737373]">por {h.requested_by}</span>
              </summary>
              <div className="mt-4">
                <ResultCard result={h.result} createdAt={h.created_at} modelLabel={h.model}/>
              </div>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
